import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  IconButton,
  Skeleton,
  Tooltip,
  Pagination,
  Button
} from '@mui/material';
import { 
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { task, car } from '../../../utils/api';
import { TASK_STATUS, TASK_PRIORITY } from '../../../constants/task';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';

const TASKS_PER_PAGE = 5;
const FETCH_LIMIT = 20;

const TaskSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carDetails, setCarDetails] = useState({});
  const [page, setPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [allTasks, setAllTasks] = useState([]);

  useEffect(() => {
    fetchActiveTasks();
  }, []);

  const fetchActiveTasks = async () => {
    try {
      setLoading(true);
      const response = await task.getMyTasks({
        limit: FETCH_LIMIT
      });
      console.log('TaskSection - API response:', response);
      
      let taskData = [];
      if (Array.isArray(response)) {
        console.log('TaskSection - Response is an array');
        taskData = response;
      } else if (response && response.tasks && Array.isArray(response.tasks)) {
        console.log('TaskSection - Response has tasks array property');
        taskData = response.tasks;
      } else if (response && typeof response === 'object') {
        console.log('TaskSection - Response is an object, looking for arrays');
        const possibleTaskArrays = Object.values(response).filter(val => Array.isArray(val));
        console.log('TaskSection - Possible task arrays:', possibleTaskArrays);
        if (possibleTaskArrays.length > 0) {
          taskData = possibleTaskArrays.reduce((a, b) => a.length > b.length ? a : b, []);
        }
      }
      
      console.log('TaskSection - Processed task data before filtering:', taskData);
      
      // Filter active tasks client-side (pending and in_progress)
      const activeTasks = taskData.filter(task => 
        task.status === TASK_STATUS.PENDING || task.status === TASK_STATUS.IN_PROGRESS
      );
      
      console.log('TaskSection - Active tasks after filtering:', activeTasks);
      
      // Fetch full details for each task to ensure car data is properly populated
      const tasksWithDetails = await Promise.all(
        activeTasks.map(async (activeTask) => {
          try {
            const detailedTask = await task.getById(activeTask._id);
            console.log(`Detailed task for ${activeTask._id}:`, detailedTask);
            return detailedTask;
          } catch (error) {
            console.error(`Error fetching details for task ${activeTask._id}:`, error);
            return activeTask; // Fall back to the original task if details fetch fails
          }
        })
      );
      
      console.log('TaskSection - Tasks with full details:', tasksWithDetails);
      
      // Extract all possible car IDs from the tasks
      const carIds = new Set();
      
      tasksWithDetails.forEach(task => {
        if (task.car) {
          if (typeof task.car === 'object' && task.car._id) {
            carIds.add(task.car._id);
          } else if (typeof task.car === 'string') {
            carIds.add(task.car);
          }
        }
        
        if (task.carId) {
          carIds.add(task.carId);
        }
      });
      
      const carIdsArray = Array.from(carIds);
      console.log('TaskSection - Car IDs to fetch:', carIdsArray);
      
      // Create a map of car details
      const carDetailsMap = {};
      
      await Promise.all(
        carIdsArray.map(async (carId) => {
          try {
            const carData = await car.getById(carId);
            console.log(`Car details for ${carId}:`, carData);
            
            if (carData && carData.car && carData.car._id) {
              carDetailsMap[carId] = carData;
              if (carData.car._id !== carId) {
                carDetailsMap[carData.car._id] = carData.car;
              }
            } else {
              carDetailsMap[carId] = carData;
              if (carData && carData._id && carData._id !== carId) {
                carDetailsMap[carData._id] = carData;
              }
            }
          } catch (error) {
            console.error(`Error fetching car details for ${carId}:`, error);
          }
        })
      );
      
      setCarDetails(carDetailsMap);
      console.log('TaskSection - Car details map:', carDetailsMap);
      
      // Sort by priority (urgent first)
      tasksWithDetails.sort((a, b) => {
        const priorityOrder = {
          [TASK_PRIORITY.URGENT]: 0,
          [TASK_PRIORITY.HIGH]: 1,
          [TASK_PRIORITY.MEDIUM]: 2,
          [TASK_PRIORITY.LOW]: 3
        };
        
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      });

      // Store all tasks
      setAllTasks(tasksWithDetails);
      setTotalTasks(tasksWithDetails.length);
      
      // Get tasks for first page
      const paginatedTasks = tasksWithDetails.slice(0, TASKS_PER_PAGE);
      setTasks(paginatedTasks);
    } catch (error) {
      console.error('TaskSection - Error fetching tasks:', error);
      toast.error(t('error.loadTasks'));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    const startIndex = (newPage - 1) * TASKS_PER_PAGE;
    const endIndex = startIndex + TASKS_PER_PAGE;
    setTasks(allTasks.slice(startIndex, endIndex));
  };

  const handleViewAllTasks = () => {
    navigate('/workshop');
  };

  // Helper function to get car name
  const getCarName = (carRef) => {
    console.log('Getting car name for:', carRef);
    
    // Direct check for the car ID we're seeing in the logs
    if (carRef && carRef._id) {
      const carId = carRef._id;
      console.log('Looking for car with ID:', carId, 'in', carDetails);
      
      // Check if we have this car in our details map
      if (carDetails[carId]) {
        const carData = carDetails[carId];
        console.log('Found car data:', carData);
        
        // Handle the nested car structure we're seeing in the logs
        if (carData.car && carData.car.name) {
          console.log('Returning car name from nested structure:', carData.car.name);
          return carData.car.name;
        }
        
        if (carData.name) {
          console.log('Returning car name:', carData.name);
          return carData.name;
        }
      }
      
      // If we don't have the car in our details map, try to fetch it directly
      console.log('Car not found in details map, checking car object directly');
      if (carRef.name) {
        console.log('Car has name property:', carRef.name);
        return carRef.name;
      }
    }
    
    // If all else fails, return a more descriptive fallback
    console.log('Falling back to default car name');
    return 'Car';
  };

  const handleCompleteTask = async (taskId, event) => {
    event.stopPropagation();
    try {
      await task.update(taskId, { status: TASK_STATUS.COMPLETED });
      toast.success(t('success.taskCompleted'));
      fetchActiveTasks();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error(t('error.updateTask'));
    }
  };

  const navigateToTask = (taskId) => {
    navigate(`/workshop/tasks/${taskId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case TASK_STATUS.PENDING:
        return 'warning';
      case TASK_STATUS.IN_PROGRESS:
        return 'info';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case TASK_PRIORITY.URGENT:
        return 'error';
      case TASK_PRIORITY.HIGH:
        return 'warning';
      case TASK_PRIORITY.MEDIUM:
        return 'info';
      case TASK_PRIORITY.LOW:
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('dashboard.activeTasks')}
        </Typography>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={60} sx={{ my: 1 }} />
        ))}
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {t('dashboard.activeTasks')}
        </Typography>
        <Button
          variant="outlined"
          onClick={handleViewAllTasks}
          endIcon={<ArrowForwardIcon />}
        >
          {t('dashboard.viewAllTasks')}
        </Button>
      </Box>

      {tasks.length === 0 ? (
        <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
          {t('dashboard.noActiveTasks')}
        </Typography>
      ) : (
        <>
          <List>
            {tasks.map((task) => (
              <ListItem 
                key={task._id} 
                divider 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  py: 1.5
                }}
                onClick={() => navigateToTask(task._id)}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {task.dueDate && (
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(task.dueDate), 'MMM d, yyyy')}
                        </Typography>
                      )}
                      
                      <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                        {task.title}
                      </Typography>
                      
                      {task.car && (
                        <Typography variant="body2" component="div" color="text.secondary">
                          {task.car._id && carDetails[task.car._id] && carDetails[task.car._id].car && carDetails[task.car._id].car.name ? 
                            carDetails[task.car._id].car.name : 
                            getCarName(task.car)}
                        </Typography>
                      )}
                      
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip 
                          label={t(`status.${task.status.replace('_', '')}`)} 
                          size="small" 
                          color={getStatusColor(task.status)}
                        />
                        <Chip 
                          label={t(`priority.${task.priority}`)} 
                          size="small" 
                          color={getPriorityColor(task.priority)}
                        />
                      </Box>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title={t('task.complete')}>
                    <IconButton
                      edge="end"
                      aria-label="complete"
                      onClick={(e) => handleCompleteTask(task._id, e)}
                      sx={{ mr: 1 }}
                    >
                      <CheckCircleIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('task.view')}>
                    <IconButton
                      edge="end"
                      aria-label="view"
                      onClick={() => navigateToTask(task._id)}
                    >
                      <ArrowForwardIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          
          {totalTasks > TASKS_PER_PAGE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination 
                count={Math.ceil(totalTasks / TASKS_PER_PAGE)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default TaskSection; 